import React from 'react';
import './BasicTheory.css';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import theorytopbanner from './images/basictheorybanner.webp';
import tenets from './images/tenets.webp';
import itf from './images/itf.webp';
import itf2 from './images/itf2.webp';
import tkdtree from './images/tkdtree.webp';
import choi from './images/choi.webp';

const BasicTheory = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>TKD Study - Basic Theory</title>
      </Helmet>
      <div className="basic-theory">
        <div className="grey-banner">
          <img src={theorytopbanner} alt="Basic Theory Banner" />
        </div>

        <div className="back-arrow" onClick={() => navigate('/')}>
          ← Back
        </div>

        <div className="content">
          <h2>What is Taekwondo?</h2>
          <p>
            Taekwondo is the art of self-defence founded by a Korean General named Choi Hong Hi. Taekwondo was officially recognized on 11th April 1955 when General Choi, after many years of research, development, and experimentation, proclaimed Taekwondo to the world as a completely unique martial art with Korean origins.
          </p>
          <p>
            Translated literally, 'Tae' stands for jumping or flying kick or smashing with the foot. 'Kwon' denotes the fist, to punch or destroy with the hands or fist. 'Do' means art or way. It signifies The Art of the Foot and Hand.
          </p>

          <section className="tenets-section">
            <div className="tenets-text">
              <h2>Tenets of Taekwondo</h2>
              <ul>
                <li>Courtesy</li>
                <li>Integrity</li>
                <li>Perseverance</li>
                <li>Self-Control</li>
                <li>Indomitable Spirit</li>
              </ul>
            </div>
            <div className="tenets-image">
              <img src={tenets} alt="Tenets" />
            </div>
          </section>

          <h2>Oath of Taekwondo</h2>
          <ul>
            <li>I shall observe the tenets of Taekwondo.</li>
            <li>Respect all seniors and instructors.</li>
            <li>Never misuse Taekwondo.</li>
            <li>Be a champion of freedom and justice.</li>
            <li>Help to build a more peaceful world.</li>
          </ul>

          <h2>General Choi Hong Hi IX Degree</h2>
          <section className="choiimage-section">
            <img src={choi} alt="General Choi Hong Hi" />
          </section>

          <h2>Origin of Taekwondo</h2>
          <p>
            General Choi Hong Hi was born on November 9th, 1918, in the Hwa Dae Myong Chu District of Korea. At the age of twelve, he began studying a traditional Korean martial art called Taek Kyon, which emphasizes foot techniques. Later, during his time in Japan, he encountered a Karate instructor who helped him achieve his first-degree Black Belt in less than two years.
          </p>
          <p>
            Driven by his beliefs and innovative approach to martial arts instruction, General Choi combined elements of Taek Kyon and Karate to create a modern martial art. He named it Taekwondo, meaning 'the way of the feet and the hands,' which was officially adopted on April 11th, 1955.
          </p>
          <p>
            In 1959, General Choi became the president of the Korean Taekwondo Association. Seven years later, on March 22nd, 1966, he founded the International Taekwondo Federation (ITF). As the founder of Taekwondo and the head of the ITF, he spread his art across the globe, making Taekwondo training accessible worldwide.
          </p>
          <p>
            Having dedicated his life to the development of Taekwondo, a martial art based on time-honored principles and philosophy, General Choi passed away from cancer on June 15th, 2002, in his homeland.
          </p>

          <section className="images-section">
            <img src={itf2} alt="ITF Logo 2" />
            <img src={itf} alt="ITF Logo" />
          </section>

          <h2>The Taekwondo Tree</h2>
          <section className="tkdtree-section">
            <div className="tkdtree-text">
              <p>
                The shape of a tree is on the back of your dobok. The word Taekwondo is written in Korean like the trunk of a tree and in English like the canopy of the tree. The tree symbolizes a long life.
              </p>
            </div>
            <div className="tkdtree-image">
              <img src={tkdtree} alt="Taekwondo Tree" />
            </div>
          </section>

          <h2>Theory of Power</h2>
          <p><strong>Reaction Force</strong> <span className="korean">(Bandong Ryok)</span><br />
            Every technique requires a reactive force. A punch with the right fist is aided by pulling back the left fist to the hip.
          </p>
          <p><strong>Concentration</strong> <span className="korean">(Jip Joong)</span><br />
            To increase the effect and force of a technique, apply the impact to the smallest area. Punch with the two front knuckles rather than the full fist, or kick with the ball of the foot instead of the entire foot.
          </p>
          <p><strong>Equilibrium</strong> <span className="korean">(Kyun Hyung)</span><br />
            Keep the body well balanced for more effective strikes. A stance should always be stable and flexible. An unbalanced body is easily toppled.
          </p>
          <p><strong>Breath Control</strong> <span className="korean">(Hohup Jojul)</span><br />
            Controlling your breathing not only increases stamina but helps the body to absorb a strike. Exhaling sharply at the end of a move maximizes effort. Breath control also conceals signs of fatigue.
          </p>
          <p><strong>Mass</strong> <span className="korean">(Zilyang)</span><br />
            More mass behind a technique increases its power. Twisting the hips and rotating the knees puts more mass behind a technique, which is the basis of sine motion.
          </p>
          <p><strong>Speed</strong> <span className="korean">(Sokdo)</span><br />
            Speed is essential for increasing power. Thrusting techniques are done in a linear motion across the body to maximize distance and build speed. Circular motion is used to increase speed in striking techniques.
          </p>
        </div>
      </div>
    </>
  );
};

export default BasicTheory;
